import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle, cloneElement } from "react";
import PropTypes from "prop-types";
import { useRect, Rect } from "@reach/rect";
import { TooltipPopup } from "@reach/tooltip";
import { AnimatePresence, motion } from "framer-motion";
import { easeQuadOut } from "d3-ease";
import { TooltipBox, TooltipSupplementary } from "../tooltip/tooltip.styles.es.js";
import "@reach/auto-id";
import "lodash/fp";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import { useOnClickOutside } from "../../hooks/use-on-click-outside.es.js";
import "compute-scroll-into-view";
import { adjustPlacement } from "../../utils/adjust-placement.es.js";
import { usePopoverPosition } from "../../hooks/use-popover-position.es.js";
const propTypes = {
  "aria-label": PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  dismissOnKeyPress: PropTypes.bool,
  justify: PropTypes.oneOf(["start", "end", "center"]),
  maxWidth: PropTypes.number,
  offset: PropTypes.number,
  onDismiss: PropTypes.func,
  placement: PropTypes.oneOf(["bottom", "left", "right", "top"]),
  showPrompt: PropTypes.bool,
  showTriangle: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
};
const defaultProps = {
  "aria-label": void 0,
  disabled: false,
  dismissOnKeyPress: true,
  justify: "center",
  maxWidth: void 0,
  offset: 4,
  onDismiss: void 0,
  placement: "bottom",
  showPrompt: true,
  showTriangle: true,
  subtitle: void 0
};
const Prompt = forwardRef(
  ({
    "aria-label": ariaLabel,
    children,
    disabled,
    dismissOnKeyPress,
    justify,
    maxWidth,
    offset,
    onDismiss,
    placement,
    showPrompt,
    showTriangle,
    subtitle,
    title
  }, forwardedRef) => {
    const ref = useRef();
    const rect = useRect(ref);
    const triggerRect = useRect(ref);
    const [visible, setVisible] = useState(true);
    const popoverPosition = usePopoverPosition({ placement, justify, offset });
    const calculatePlacement = useMemo(
      () => adjustPlacement(placement, offset),
      [offset, placement]
    );
    const calculatePosition = useCallback(
      (...args) => ({
        ...popoverPosition(...args),
        position: "absolute"
      }),
      [popoverPosition]
    );
    const disappear = useCallback(() => {
      if (showPrompt && visible) {
        setVisible(false);
        if (onDismiss) {
          onDismiss();
        }
      }
    }, [onDismiss, showPrompt, visible]);
    useOnClickOutside(ref, disappear);
    const handleKeyPress = useCallback(() => {
      if (dismissOnKeyPress) {
        disappear();
      }
    }, [disappear, dismissOnKeyPress]);
    useEffect(() => {
      document.addEventListener("keypress", handleKeyPress);
      return () => {
        document.removeEventListener("keypress", handleKeyPress);
      };
    }, [handleKeyPress]);
    useImperativeHandle(forwardedRef, () => ref.current);
    if (disabled) {
      return children;
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      cloneElement(children, { ref }),
      /* @__PURE__ */ jsx(AnimatePresence, { initial: false, children: showPrompt && visible && /* @__PURE__ */ jsx(
        TooltipPopup,
        {
          "aria-label": ariaLabel,
          isVisible: true,
          label: /* @__PURE__ */ jsx(Rect, { children: ({ ref: popupRef, rect: popupRect }) => /* @__PURE__ */ jsx(
            motion.div,
            {
              ref: popupRef,
              style: { transformOrigin: "inherit" },
              initial: { opacity: 0, transform: "scale(0.8)" },
              animate: { opacity: 1, transform: "scale(1)" },
              exit: { opacity: 0, transform: "scale(0.8)" },
              transition: { ease: easeQuadOut, duration: 0.1 },
              children: /* @__PURE__ */ jsxs(
                TooltipBox,
                {
                  $justify: justify,
                  $maxWidth: maxWidth,
                  $placement: calculatePlacement(rect, popupRect),
                  $showTriangle: showTriangle,
                  children: [
                    title,
                    subtitle && /* @__PURE__ */ jsx(TooltipSupplementary, { children: subtitle })
                  ]
                }
              )
            }
          ) }),
          position: calculatePosition,
          triggerRect
        }
      ) })
    ] });
  }
);
Prompt.propTypes = propTypes;
Prompt.defaultProps = defaultProps;
export {
  Prompt
};
